/**
 * Analytics Service
 *
 * Tracks user activity and app metrics for offline-first PWA.
 * Events are queued locally and synced to the server when online.
 */

import db from '@/db';
import { fetchWithAuth } from '../../lib/fetch-utils';
import { AnalyticsEvent, AnalyticsEventType, AnalyticsEventData } from '../types/analytics_event';
import { getCurrentPerson } from './user_service';
import { API_ENDPOINTS, ANALYTICS_DISABLED, APP_VERSION } from '../../config/env';
import { createLogger, LogLevel } from '../../lib/logger';
import { isOnline } from '../../lib/connectivity';

const logger = createLogger('Analytics', LogLevel.DEBUG);

const BATCH_SIZE = 100; // Sync up to 100 events at a time
const MAX_RETRY_ATTEMPTS = 3;

/**
 * Get current storage metrics from IndexedDB
 */
export async function getStorageMetrics(): Promise<{
  usage_bytes: number;
  quota_bytes: number;
  usage_percentage: number;
}> {
  if ('storage' in navigator && navigator.storage && 'estimate' in navigator.storage) {
    const estimate = await navigator.storage.estimate();
    const usage = estimate.usage || 0;
    const quota = estimate.quota || 0;
    const percentage = quota > 0 ? (usage / quota) * 100 : 0;

    return {
      usage_bytes: usage,
      quota_bytes: quota,
      usage_percentage: Math.round(percentage * 100) / 100,
    };
  }

  return {
    usage_bytes: 0,
    quota_bytes: 0,
    usage_percentage: 0,
  };
}

/**
 * Get app-level metrics
 */
export async function getAppMetrics(): Promise<{
  total_clients: number;
  total_documents: number;
  unsynced_documents: number;
}> {
  const [
    totalClients,
    totalDocuments,
    unsyncedDocuments,
  ] = await Promise.all([
    db.clients.count(),
    db.documents.count(),
    db.documents.filter(doc => !doc.synced).count(),
  ]);

  return {
    total_clients: totalClients,
    total_documents: totalDocuments,
    unsynced_documents: unsyncedDocuments,
  };
}

/**
 * Detect device type based on screen size and user agent
 */
function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  const width = window.innerWidth;
  const userAgent = navigator.userAgent.toLowerCase();

  // Check if tablet
  if ((width >= 768 && width < 1024) || /ipad|tablet/.test(userAgent)) {
    return 'tablet';
  }

  // Check if mobile
  if (width < 768 || /mobile|android|iphone/.test(userAgent)) {
    return 'mobile';
  }

  return 'desktop';
}

/**
 * Check if app is installed as PWA
 */
export function isAppInstalled(): boolean {
  // Check if matchMedia is available
  if (typeof window !== 'undefined' && window.matchMedia) {
    // Check if running in standalone mode (installed PWA)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }
  }

  // Check for iOS standalone mode
  if ((navigator as any).standalone === true) {
    return true;
  }

  return false;
}

/**
 * Get app version from package.json or environment
 */
function getAppVersion(): string {
  return APP_VERSION;
}

/**
 * Snapshot of the person to attribute an event to. Use when the caller has
 * lost auth state before reaching the emit — e.g. the document.sync.failure
 * path emitted after a 401, where fetchWithAuth clears current_person before
 * throwing AuthExpiredError. Capture this BEFORE the call that triggers the
 * clear (typically `fetchWithAuth`).
 */
export interface PersonSnapshot {
  personId: number;
  organizationId: number;
}

interface QueueAnalyticsEventArgs {
  eventType: AnalyticsEventType;
  data?: AnalyticsEventData;
  personId: number;
  organizationId: number;
}

async function queueAnalyticsEvent({ eventType, data, personId, organizationId }: QueueAnalyticsEventArgs): Promise<void> {
  if (!personId || !organizationId) {
    logger.warn(' Missing person or organization ID, skipping event tracking');
    return;
  }

  const event: AnalyticsEvent = {
    id: crypto.randomUUID(),
    event_type: eventType,
    timestamp: new Date().toISOString(),
    person_id: personId,
    organization_id: organizationId,
    data: data || {},
    app_version: getAppVersion(),
    device_type: getDeviceType(),
    is_online: navigator.onLine,
    synced: false,
    sync_attempts: 0,
    created_at: new Date().toISOString(),
  };

  await db.analyticsEvents.add(event);

  if (isOnline()) {
    syncAnalyticsEvents().catch(err => {
      logger.error(' Background sync failed:', err);
    });
  }
}

/**
 * Track an analytics event using the current person from applicationSession.
 * If no current person is set (locked DB, fresh install pre-org-selection,
 * or any post-401 / post-softSignOut state) the event is SILENTLY DROPPED.
 *
 * Use this for the casual path — heartbeats, client.viewed, document.edited,
 * etc. — where the caller is unambiguously inside an authenticated session.
 *
 * **If you're emitting from a catch that handles AuthExpiredError, or any
 * code path that runs after `fetchWithAuth` may have cleared current_person,
 * use {@link trackEventWithPerson} instead** — capture the person snapshot
 * BEFORE the call that triggers the clear, then pass it through. The type
 * system can't catch this for you; see docs/ANALYTICS_EVENTS.md "Gotcha"
 * section for the reference pattern.
 */
export async function trackEvent(
  eventType: AnalyticsEventType,
  data?: AnalyticsEventData,
): Promise<void> {
  if (ANALYTICS_DISABLED) return;

  try {
    // Check if database is open (it's closed when screen is locked)
    if (!db.isOpen()) {
      logger.debug(' Database is closed (screen locked), skipping event tracking');
      return;
    }

    const currentPerson = await getCurrentPerson();
    if (!currentPerson) {
      logger.warn(' No current person found, skipping event tracking');
      return;
    }

    await queueAnalyticsEvent({
      eventType,
      data,
      personId: currentPerson.id,
      organizationId: currentPerson.organization.id,
    });
  } catch (error) {
    logger.error(' Failed to track event:', error);
  }
}

/**
 * Track an analytics event using an explicit person snapshot. The person
 * argument is required — its presence is what tells the reader "this caller
 * knows it might be running in an auth-cleared window and is preserving
 * attribution deliberately."
 *
 * Reference pattern (see syncDocument in src/db/services/document_service.ts):
 *
 * ```ts
 * const snapshot = await getCurrentPerson();
 * try {
 *   await fetchWithAuth(url, opts, () => softSignOut());  // may clear current_person
 * } catch (err) {
 *   if (snapshot) {
 *     trackEventWithPerson(
 *       'document.sync.failure',
 *       { sync_error_type: 'auth_expired', ... },
 *       { personId: snapshot.id, organizationId: snapshot.organization.id },
 *     );
 *   } else {
 *     trackEvent('document.sync.failure', { ... });  // happens to have no snapshot — fine
 *   }
 * }
 * ```
 */
export async function trackEventWithPerson(
  eventType: AnalyticsEventType,
  data: AnalyticsEventData | undefined,
  person: PersonSnapshot,
): Promise<void> {
  if (ANALYTICS_DISABLED) return;

  try {
    if (!db.isOpen()) {
      logger.debug(' Database is closed (screen locked), skipping event tracking');
      return;
    }

    await queueAnalyticsEvent({
      eventType,
      data,
      personId: person.personId,
      organizationId: person.organizationId,
    });
  } catch (error) {
    logger.error(' Failed to track event:', error);
  }
}

// Coalesces overlapping analytics sweeps: a reconnect can trigger the sweep
// from several paths at once (REFRESH, the SYNC fallback, the session-tracker
// flush), and parallel sweeps would read the same unsynced snapshot and POST
// each event twice. No rerun flag is needed — stragglers are covered by the
// SYNC_ANALYTICS debounce and the next flush.
let syncAnalyticsInFlight: Promise<number> | null = null;

/**
 * Sync pending analytics events to the server
 * Returns the number of events successfully synced
 */
export function syncAnalyticsEvents(): Promise<number> {
  if (syncAnalyticsInFlight) return syncAnalyticsInFlight;
  syncAnalyticsInFlight = runAnalyticsSweep().finally(() => {
    syncAnalyticsInFlight = null;
  });
  return syncAnalyticsInFlight;
}

async function runAnalyticsSweep(): Promise<number> {
  if (ANALYTICS_DISABLED) return 0;

  if (!isOnline()) {
    logger.debug(' Offline - skipping sync');
    return 0;
  }

  try {
    // Get unsynced events (up to BATCH_SIZE)
    const unsyncedEvents = await db.analyticsEvents
      .filter(event => !event.synced && event.sync_attempts < MAX_RETRY_ATTEMPTS)
      .limit(BATCH_SIZE)
      .toArray();

    if (unsyncedEvents.length === 0) {
      return 0;
    }

    logger.debug(` Syncing ${unsyncedEvents.length} events to server`);

    let syncedCount = 0;

    // Send events one at a time (could be batched in the future)
    for (const event of unsyncedEvents) {
      try {
        const response = await fetchWithAuth(`${API_ENDPOINTS.analytics}?current_person_id=${event.person_id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            analytic: {
              person_id: event.person_id,
              event_type: event.event_type,
              timestamp: event.timestamp,
              data: event.data,
              app_version: event.app_version,
              device_type: event.device_type,
              is_online: event.is_online,
            }
          }),
        });

        if (response.ok) {
          // Mark as synced
          await db.analyticsEvents.update(event.id, {
            synced: true,
            last_sync_attempt: new Date().toISOString(),
          });
          syncedCount++;
        } else {
          // Increment retry count
          await db.analyticsEvents.update(event.id, {
            sync_attempts: event.sync_attempts + 1,
            last_sync_attempt: new Date().toISOString(),
            error_message: `HTTP ${response.status}`,
          });
        }
      } catch (error) {
        logger.error(' Failed to sync event:', error);
        // Increment retry count
        await db.analyticsEvents.update(event.id, {
          sync_attempts: event.sync_attempts + 1,
          last_sync_attempt: new Date().toISOString(),
          error_message: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    }

    logger.debug(` Successfully synced ${syncedCount}/${unsyncedEvents.length} events`);

    // Clean up old synced events (keep last 1000)
    const syncedEvents = await db.analyticsEvents
      .filter(event => event.synced)
      .reverse()
      .sortBy('created_at');

    if (syncedEvents.length > 1000) {
      const eventsToDelete = syncedEvents.slice(1000);
      await db.analyticsEvents.bulkDelete(eventsToDelete.map(e => e.id));
      logger.debug(` Cleaned up ${eventsToDelete.length} old events`);
    }

    return syncedCount;
  } catch (error) {
    logger.error(' Sync failed:', error);
    return 0;
  }
}

/**
 * Get count of pending analytics events
 */
export async function getPendingAnalyticsCount(): Promise<number> {
  return await db.analyticsEvents
    .filter(event => !event.synced)
    .count();
}

/**
 * Clear all analytics events (for testing)
 */
export async function clearAllAnalyticsEvents(): Promise<void> {
  await db.analyticsEvents.clear();
}

/**
 * Track storage check with current metrics.
 * @param trigger - What triggered the storage check. Free-text label, not an
 *   event name. Current callers pass `'unlock'` (LockContext.unlockScreen) and
 *   `'periodic'` (SessionTracker's storage-check interval). Use a similarly
 *   short, lowercase, underscored label for new call sites.
 */
export async function trackStorageCheck(trigger?: string): Promise<void> {
  try {
    // Note: DB open check is handled by trackEvent(), no need to duplicate here
    const storageMetrics = await getStorageMetrics();
    const appMetrics = await getAppMetrics();
    const sessionIdItem = await db.applicationSession.get('current_session_id');

    await trackEvent('storage.check', {
      session_id: sessionIdItem?.value?.toString() || undefined,
      trigger,
      ...storageMetrics,
      ...appMetrics,
    });

    logger.debug(
      `Storage check (${trigger || 'unknown'}): ${(storageMetrics.usage_bytes / 1024 / 1024).toFixed(2)} MB used (${storageMetrics.usage_percentage.toFixed(1)}%)`
    );
  } catch (error) {
    logger.error(' Failed to track storage check:', error);
  }
}

